import { useMemo, useState } from "react";
import { Reservation, Payment } from "@turizi/shared";
import { Badge, Loader, Tooltip, Popover } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { RESERVATION_STATUS, PAYMENT_STATUS_COLORS } from "@turizi/shared";
import { TagIcon } from "@/lib/tag-utils";
import { Link } from "react-router-dom";
import { BUSINESSES_PATH } from "@/lib/navigation";
import { usePayments } from "@/hooks/api-hooks";
import { localTenant } from "@/services/session-service";
import PaymentsTable from "./payments-table";
import { IconChevronDown, IconReceipt } from "@tabler/icons-react";


export const ReservationClient = ({
    reservation,
    icon = true,
    className = "text-inherit hover:underline",
}: {
    reservation: Reservation;
    icon?: boolean;
    className?: string;
}) => {
    const { t } = useTranslation();
    if (!reservation?.client_business) {
        return <div className="text-gray-300">{t('no_client')}</div>;
    }

    return (
        <>
            {reservation?.client_business && (
                <Link
                    target="_blank"
                    to={`${BUSINESSES_PATH}/${reservation.client_business.id}`}
                    className={`inline-flex gap-1 items-center`}
                >
                    {icon && (
                        <TagIcon name={reservation.client_business.category} noBackground hideText size="xxs" />
                    )}
                    <div className={className}>
                        {reservation?.client_business?.name || '-'}
                    </div>
                </Link>
            )}
        </>
    );
};

export const ReservationStatusBadge = ({
    reservation,
    showText = true,
    className,
}: {
    reservation: Reservation;
    showText?: boolean;
    className?: string;
}) => {
    const { t } = useTranslation();

    return (
        <Tooltip label={t(`reservation_${reservation?.status}`)} >
            <div className="flex gap-1 items-center">
                <Badge
                    circle
                    color={RESERVATION_STATUS[reservation?.status]?.color}
                    className={`!w-[10px] !h-[10px] !p-0`}
                />
                {showText && <div className={`whitespace-nowrap ${className || 'text-gray-500 text-xs'}`}>
                    {t(`reservation_${reservation?.status}`)}
                </div>}
            </div>
        </Tooltip>
    );
};

export const ReservationPaymentStatus = ({
    reservation,
    showPayments = false,
    showBadge = true,
    className,
}: {
    reservation: Reservation;
    showPayments?: boolean;
    showBadge?: boolean;
    className?: string;
}) => {
    const { t } = useTranslation();
    const [showClientPayments, setShowClientPayments] = useState(false);
    const [showProviderPayments, setShowProviderPayments] = useState(false);

    // Fetch payments of this reservation if needed
    const {
        data: paymentsRes,
        isLoading: loadingPayments
    } = usePayments(
        { reservation_id: reservation?.id },
        { enabled: showPayments && !reservation?.payments && !!reservation?.id }
    );
    const payments = reservation?.payments || paymentsRes?.data || [];

    const clientPaymentStatus = reservation?.payment_status || '**';
    const clientColor = PAYMENT_STATUS_COLORS[clientPaymentStatus] || 'gray';

    const providerPaymentStatus = useMemo(() => {
        const serviceBookings = reservation?.service_bookings || [];
        const bookingsWithCost = serviceBookings.filter(
            b => Number(b.money_out_amount || 0) > 0
        );

        if (bookingsWithCost.length === 0) {
            return 'no_costs';
        }

        const paidCount = bookingsWithCost.filter(b => b.cost_payment_status === 'paid').length;
        const partialCount = bookingsWithCost.filter(b => b.cost_payment_status === 'partially_paid' || b.cost_payment_status === 'partial').length;

        if (paidCount === bookingsWithCost.length) {
            return 'paid';
        } else if (paidCount > 0 || partialCount > 0) {
            return 'partially_paid';
        } else {
            return 'unpaid';
        }
    }, [reservation?.service_bookings]);
    const providerColor = PAYMENT_STATUS_COLORS[providerPaymentStatus] || 'gray';

    const ownedBusinessIds = useMemo(() => {
        return localTenant()?.businesses?.map(b => b.id) || [];
    }, []);

    const clientPayments = useMemo(() => {
        if (!payments || payments.length === 0) return [];
        return payments.filter(p => {
            if (ownedBusinessIds.length > 0) {
                const isProviderPayout = ownedBusinessIds.includes(p.from_business_id || -1) &&
                    p.to_business_id && !ownedBusinessIds.includes(p.to_business_id);
                return !isProviderPayout;
            }
            return p.from_business_id === reservation?.client_id_business || !p.to_business_id;
        });
    }, [payments, ownedBusinessIds, reservation?.client_id_business]);

    const providerPayments = useMemo(() => {
        if (!payments || payments.length === 0) return [];
        return payments.filter(p => {
            if (ownedBusinessIds.length > 0) {
                return ownedBusinessIds.includes(p.from_business_id || -1) &&
                    p.to_business_id && !ownedBusinessIds.includes(p.to_business_id);
            }
            return p.to_business_id && p.to_business_id !== reservation?.client_id_business;
        });
    }, [payments, ownedBusinessIds, reservation?.client_id_business]);


    return (
        <div className="flex flex-col gap-3">
            {showBadge && !showPayments && (
                <div className={className || 'flex flex-wrap items-center gap-3'}>
                    <div className="flex items-center gap-1.5 text-xxs shrink-0">
                        <span className="text-gray-500 font-medium whitespace-nowrap">{t('client')}:</span>
                        <Badge color={clientColor} variant="light" size="sm" className="shrink-0">
                            {t(clientPaymentStatus)}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-1.5 text-xxs shrink-0">
                        <span className="text-gray-500 font-medium whitespace-nowrap">{t('provider')}:</span>
                        <Badge color={providerColor} variant="light" size="sm" className="shrink-0">
                            {t(providerPaymentStatus)}
                        </Badge>
                    </div>
                </div>
            )}
            {showPayments && (
                <div className="flex flex-col gap-2 w-full">
                    {/* Client Payment Popover */}
                    <Popover
                        opened={showClientPayments}
                        onChange={setShowClientPayments}
                        shadow="xl"
                        radius="lg"
                        withArrow
                    >
                        <Popover.Target>
                            <div
                                onClick={() => setShowClientPayments((prev) => !prev)}
                                className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100/90 border border-slate-200/90 hover:border-slate-300 transition-all cursor-pointer select-none group active:scale-[0.98] shadow-2xs whitespace-nowrap"
                            >
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider group-hover:text-slate-900 w-20 shrink-0">
                                        {t('client')}
                                    </span>
                                    <Badge color={clientColor} variant="light" size="sm" className="shrink-0 font-bold shadow-2xs">
                                        {t(clientPaymentStatus)}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200/70 text-slate-600 group-hover:bg-slate-300/80 transition-colors whitespace-nowrap">
                                        {clientPayments.length} {clientPayments.length === 1 ? t('payment') : t('payments')}
                                    </span>
                                    <IconChevronDown
                                        size={14}
                                        className={`text-slate-400 group-hover:text-slate-600 transition-transform duration-200 shrink-0 ${showClientPayments ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown className="p-3 max-w-lg">
                            <div className="flex items-center justify-between gap-3 pb-2 border-b border-slate-100 mb-2.5">
                                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                    <IconReceipt size={15} className="text-slate-500" />
                                    {t('client')} - {t('linked_payments')} ({clientPayments.length})
                                </span>
                            </div>
                            {loadingPayments ? (
                                <div className="flex justify-center py-4"><Loader size="xs" color="black" /></div>
                            ) : clientPayments.length === 0 ? (
                                <div className="text-xs text-slate-400 py-3 text-center italic">{t('no_client_payments')}</div>
                            ) : (
                                <PaymentsTable showHeader={false} payments={clientPayments} />
                            )}
                        </Popover.Dropdown>
                    </Popover>

                    {/* Provider Payment Popover */}
                    <Popover
                        opened={showProviderPayments}
                        onChange={setShowProviderPayments}
                        shadow="xl"
                        radius="lg"
                        withArrow
                    >
                        <Popover.Target>
                            <div
                                onClick={() => setShowProviderPayments((prev) => !prev)}
                                className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100/90 border border-slate-200/90 hover:border-slate-300 transition-all cursor-pointer select-none group active:scale-[0.98] shadow-2xs whitespace-nowrap"
                            >
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider group-hover:text-slate-900 w-20 shrink-0">
                                        {t('providers')}
                                    </span>
                                    <Badge color={providerColor} variant="light" size="sm" className="shrink-0 font-bold shadow-2xs">
                                        {t(providerPaymentStatus)}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200/70 text-slate-600 group-hover:bg-slate-300/80 transition-colors whitespace-nowrap">
                                        {providerPayments.length} {providerPayments.length === 1 ? t('payment') : t('payments')}
                                    </span>
                                    <IconChevronDown
                                        size={14}
                                        className={`text-slate-400 group-hover:text-slate-600 transition-transform duration-200 shrink-0 ${showProviderPayments ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown className="p-3 max-w-lg">
                            <div className="flex items-center justify-between gap-3 pb-2 border-b border-slate-100 mb-2.5">
                                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                    <IconReceipt size={15} className="text-slate-500" />
                                    {t('providers')} - {t('linked_payments')} ({providerPayments.length})
                                </span>
                            </div>
                            {loadingPayments ? (
                                <div className="flex justify-center py-4"><Loader size="xs" color="black" /></div>
                            ) : providerPayments.length === 0 ? (
                                <div className="text-xs text-slate-400 py-3 text-center italic">{t('no_provider_payments')}</div>
                            ) : (
                                <PaymentsTable showHeader={false} payments={providerPayments} />
                            )}
                        </Popover.Dropdown>
                    </Popover>
                </div>
            )}
        </div>
    );
};

export const ReservationPaymentStatusSmall = ({
    reservation,
    className,
}: {
    reservation: Reservation;
    className?: string;
}) => {
    const { t } = useTranslation();
    const clientPaymentStatus = reservation?.payment_status || '**';
    const clientColor = PAYMENT_STATUS_COLORS[clientPaymentStatus] || 'gray';

    const providerPaymentStatus = useMemo(() => {
        const serviceBookings = reservation?.service_bookings || [];
        const bookingsWithCost = serviceBookings.filter(
            b => Number(b.money_out_amount || 0) > 0
        );

        if (bookingsWithCost.length === 0) {
            return 'no_costs';
        }

        const paidCount = bookingsWithCost.filter(b => b.cost_payment_status === 'paid').length;
        const partialCount = bookingsWithCost.filter(b => b.cost_payment_status === 'partially_paid' || b.cost_payment_status === 'partial').length;

        if (paidCount === bookingsWithCost.length) {
            return 'paid';
        } else if (paidCount > 0 || partialCount > 0) {
            return 'partially_paid';
        } else {
            return 'unpaid';
        }
    }, [reservation?.service_bookings]);
    const providerColor = PAYMENT_STATUS_COLORS[providerPaymentStatus] || 'gray';

    return (
        <div className="flex flex-col gap-3">
            <div className={className || 'flex flex-wrap items-center gap-3'}>
                <div className="flex items-center gap-1.5 text-xxs shrink-0">
                    {/* <span className="text-gray-500 font-medium whitespace-nowrap">{t('client')}:</span> */}
                    <Badge color={clientColor} variant="light" size="sm" className="shrink-0">
                        {t(clientPaymentStatus)}
                    </Badge>
                </div>
                {/* <div className="flex items-center gap-1.5 text-xxs shrink-0">
                    <span className="text-gray-500 font-medium whitespace-nowrap">{t('provider')}:</span>
                    <Badge color={providerColor} variant="light" size="sm" className="shrink-0">
                        {t(providerPaymentStatus)}
                    </Badge>
                </div> */}
            </div>
        </div>
    );
};

